import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import {
  BasketApplianceItemQuoteRequest,
  BasketItemApplianceDetails,
  BasketItemQuoteDetails,
  BasketService,
} from '@basket';
import {
  CheckoutCompletedItem,
  CheckoutCompletedQuote,
  CheckoutDirectDebitPaymentDetails,
  CheckoutItemDetails,
  CheckoutRequest,
  CheckoutResponse,
  formatCheckoutCompletedItems,
  ValidateDirectDebitPayload,
  ValidateDirectDebitResponse,
} from '@checkout';
import { buildMultiQuoteHttpOptions } from '@common';
import { CONFIG } from '@config';
import { CustomerService } from '@customer';
import { format, parseISO } from 'date-fns';
import { map, Observable, of, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CheckoutService {
  private readonly basketService = inject(BasketService);
  private readonly config = inject(CONFIG);
  private readonly customerService = inject(CustomerService);
  private readonly httpClient: HttpClient = inject(HttpClient);
  private multiPlanEnabled = Boolean(
    this.config.domainFeatures?.multiPlanEnabled,
  );
  private clpEnabled = Boolean(this.config.form?.clpEnabled);

  // === Private Signals ===
  private readonly _checkoutDirectDebitPaymentDetails =
    signal<CheckoutDirectDebitPaymentDetails | null>(null);
  private readonly _validAccountNumberSortCode = signal<boolean>(false);
  private readonly _completedItems = signal<CheckoutCompletedItem | null>(null);

  // === Public Signals ===
  readonly validAccountNumberSortCode =
    this._validAccountNumberSortCode.asReadonly();

  readonly checkoutDirectDebitPaymentDetails =
    computed<CheckoutDirectDebitPaymentDetails>(() => {
      const checkoutDirectDebitPaymentDetails =
        this._checkoutDirectDebitPaymentDetails();
      return checkoutDirectDebitPaymentDetails as CheckoutDirectDebitPaymentDetails;
    });

  readonly checkoutItemDetails = computed<CheckoutItemDetails>(() => {
    return {
      fee: this.basketService.basketItemQuoteDetailsPaymentOption().fee,
      itemId: this.basketService.basketItem().itemId,
      itemType: this.basketService.basketItem().itemType,
      paymentTerm: this.basketService.basketItemQuoteDetails().paymentTerm,
      periodOfCover: this.basketService.basketItemQuoteDetails().periodOfCover,
      quoteId: this.basketService.basketItemQuoteDetails().quoteId,
    };
  });

  readonly checkoutRequest = computed<CheckoutRequest>(() => {
    return {
      basketId: this.basketService.basketId(),
      customer: this.customerService.customerDetails(),
      items: [this.checkoutItemDetails()],
      payment: this.checkoutDirectDebitPaymentDetails(),
    };
  });

  readonly planNumber = computed<string>(() => {
    const completedItems = this._completedItems();
    return completedItems?.confirmation.planNumber as string;
  });

  readonly companyCode = computed<string>(() => this.quote().companyCode);
  readonly schemeCode = computed<string>(() => this.quote().schemeCode);

  readonly planStartDate = computed<string>(() => {
    const completedItems = this._completedItems();
    const breakdownStartDate: Date = parseISO(
      completedItems?.confirmation.breakdownStartDate as string,
    );
    return format(breakdownStartDate, 'do MMMM yyyy');
  });

  readonly applianceDetails = computed<BasketItemApplianceDetails>(() => {
    const completedItems = this._completedItems();
    return completedItems?.item.data
      .applianceDetails as BasketItemApplianceDetails;
  });

  readonly bundleAppliances = computed<BasketApplianceItemQuoteRequest[]>(
    () => this.applianceDetails().bundleAppliances,
  );

  readonly quote = computed<BasketItemQuoteDetails>(() => {
    const completedItems = this._completedItems();
    return completedItems?.item.data.quotes[0] as BasketItemQuoteDetails;
  });

  // === Mutation methods ===
  setValidAccountNumberSortCode(isValid: boolean): void {
    this._validAccountNumberSortCode.set(isValid);
  }

  setCheckoutDirectDebitPaymentDetails(
    checkoutDirectDebitPaymentDetails: CheckoutDirectDebitPaymentDetails | null,
  ): void {
    this._checkoutDirectDebitPaymentDetails.set(
      checkoutDirectDebitPaymentDetails,
    );
  }

  // === API calls ===
  validateAccountNumberSortCode({
    accountNumber,
    sortCode,
    iban,
    paymentToken,
  }: {
    accountNumber?: number | string | null;
    sortCode?: number | string | null;
    iban?: string | null;
    paymentToken?: string | null;
  }): Observable<boolean> {
    if (paymentToken) {
      return of(true);
    }

    const payload: ValidateDirectDebitPayload = {
      accountNumber: accountNumber?.toString(),
      branchCode: sortCode?.toString(),
      iban: iban?.toUpperCase(),
    };

    return this.httpClient
      .post<ValidateDirectDebitResponse>(
        `${this.config.commonApi}/v2/direct-debit/validate`,
        payload,
      )
      .pipe(
        map((response: ValidateDirectDebitResponse) => response.result.isValid),
      );
  }

  checkout(): Observable<CheckoutResponse> {
    const version =
      this.basketService.isMultiPlanJourney() || !this.clpEnabled ? 'v1' : 'v2';

    const options = buildMultiQuoteHttpOptions(
      this.multiPlanEnabled && this.basketService.isMultiPlanJourney(),
    );

    return this.httpClient
      .post<{
        result: CheckoutResponse;
      }>(
        `${this.config.basketApi}/${version}/checkout`,
        this.checkoutRequest(),
        options,
      )
      .pipe(
        map((response: { result: CheckoutResponse }) => ({
          ...response.result,
          completedItems: formatCheckoutCompletedItems(
            this.checkoutRequest(),
            response.result,
          ),
        })),
        tap((response: CheckoutResponse<CheckoutCompletedQuote>) => {
          this._completedItems.set(response.completedItems[0]);
        }),
      );
  }
}
